/**
 * Loyalty Signup (CB) - In-store QR enrollment form
 * Version: 1.1.0
 *
 * Custom element that handles access code gating, form validation,
 * API submission, and sessionStorage persistence for the Step Ahead
 * loyalty signup. Access code check runs client-side to avoid
 * Shopify CDN caching issues with server-side Liquid checks.
 */

if (typeof CBLoyaltySignup === 'undefined') {

  class CBLoyaltySignup extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.submitBtn = null;
      this.successDiv = null;
      this.errorDiv = null;
      this.gatedContent = null;
      this.noAccessDiv = null;
      this.apiUrl = '';
    }

    connectedCallback() {
      this.apiUrl = this.dataset.apiUrl || '';
      this.gatedContent = this.querySelector('.cb-loyalty-signup__gated-content');
      this.noAccessDiv = this.querySelector('.cb-loyalty-signup__no-access');
      this.form = this.querySelector('.cb-loyalty-signup__form');
      this.submitBtn = this.querySelector('.cb-loyalty-signup__submit');
      this.successDiv = this.querySelector('.cb-loyalty-signup__success');
      this.errorDiv = this.querySelector('.cb-loyalty-signup__error');

      // Bind submit handler immediately so native form submission never leaks PII into the URL
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }

      // Check access code before showing anything
      this.checkAccess().then(function(granted) {
        if (!granted) {
          if (this.noAccessDiv) this.noAccessDiv.hidden = false;
          return;
        }

        // Valid access - show the form content
        if (this.gatedContent) this.gatedContent.hidden = false;

        // Check if already enrolled
        if (sessionStorage.getItem('cb-loyalty-enrolled') === 'true') {
          this.showSuccess();
          return;
        }
      }.bind(this));
    }

    checkAccess() {
      // Always grant access in Shopify theme editor
      if (window.Shopify && window.Shopify.designMode) {
        return Promise.resolve(true);
      }

      var hashesStr = (this.dataset.accessHashes || '').trim();

      // No hashes configured = open access
      if (!hashesStr) {
        return Promise.resolve(true);
      }

      // Read ?code= from the URL
      var params = new URLSearchParams(window.location.search);
      var urlCode = params.get('code');

      if (!urlCode) {
        return Promise.resolve(false);
      }

      // Hash the URL code with SHA-256 and compare against stored hashes
      var validHashes = hashesStr.split(',');
      return this.sha256(urlCode).then(function(urlCodeHash) {
        for (var i = 0; i < validHashes.length; i++) {
          if (validHashes[i].trim() === urlCodeHash) {
            return true;
          }
        }
        return false;
      });
    }

    sha256(str) {
      var buffer = new TextEncoder().encode(str);
      return crypto.subtle.digest('SHA-256', buffer).then(function(hash) {
        return Array.from(new Uint8Array(hash))
          .map(function(b) { return b.toString(16).padStart(2, '0'); })
          .join('');
      });
    }

    handleSubmit(e) {
      e.preventDefault();
      this.hideMessages();

      var firstName = this.form.querySelector('[name="firstName"]').value.trim();
      var lastName = this.form.querySelector('[name="lastName"]').value.trim();
      var email = this.form.querySelector('[name="email"]').value.trim();
      var phone = this.form.querySelector('[name="phone"]').value.trim();
      var zipCode = this.form.querySelector('[name="zipCode"]').value.trim();
      var marketingOptIn = this.form.querySelector('[name="marketingOptIn"]').checked;
      var website = this.form.querySelector('[name="website"]').value;

      // Honeypot check
      if (website) {
        this.showSuccess();
        return;
      }

      // Validation
      if (!firstName) {
        this.showError('Please enter your first name.');
        return;
      }
      if (!lastName) {
        this.showError('Please enter your last name.');
        return;
      }
      if (!email) {
        this.showError('Please enter your email address.');
        return;
      }
      if (!this.isValidEmail(email)) {
        this.showError('Please enter a valid email address.');
        return;
      }
      if (!zipCode) {
        this.showError('Please enter your ZIP code.');
        return;
      }
      if (!this.isValidZip(zipCode)) {
        this.showError('Please enter a valid ZIP code.');
        return;
      }

      // Set loading state
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = 'Enrolling...';
      this.submitBtn.classList.add('cb-loyalty-signup__submit--loading');

      var body = {
        fullName: firstName + ' ' + lastName,
        email: email,
        zipCode: zipCode,
        marketingOptIn: marketingOptIn
      };
      if (phone) body.phone = phone;

      fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function(response) {
          var status = response.status;

          // 200, 201, 204 are all success
          if (status >= 200 && status < 300) {
            sessionStorage.setItem('cb-loyalty-enrolled', 'true');
            this.showSuccess();
            return;
          }

          // Parse error body for message
          return response.json().then(function(data) {
            if (status === 409) {
              this.showError(data.message || 'This email is already enrolled in the loyalty program.');
            } else if (status === 429) {
              this.showError('Too many requests. Please wait a few minutes and try again.');
            } else {
              this.showError(data.message || 'Something went wrong. Please try again.');
            }
            this.resetButton();
          }.bind(this)).catch(function() {
            this.showError('Something went wrong. Please try again.');
            this.resetButton();
          }.bind(this));
        }.bind(this))
        .catch(function() {
          this.showError('Unable to connect. Please check your internet connection and try again.');
          this.resetButton();
        }.bind(this));
    }

    showSuccess() {
      if (this.form) this.form.hidden = true;
      if (this.errorDiv) this.errorDiv.hidden = true;
      if (this.successDiv) {
        var heading = this.successDiv.querySelector('.cb-loyalty-signup__success-heading');
        var message = this.successDiv.querySelector('.cb-loyalty-signup__success-message');
        if (heading) heading.textContent = this.dataset.successHeading || 'Welcome to Step Ahead Loyalty!';
        if (message) message.textContent = this.dataset.successMessage || 'Check your email for your membership details.';
        this.successDiv.hidden = false;
      }
    }

    showError(msg) {
      if (this.errorDiv) {
        var messageEl = this.errorDiv.querySelector('.cb-loyalty-signup__error-message');
        if (messageEl) messageEl.textContent = msg;
        this.errorDiv.hidden = false;
      }
    }

    hideMessages() {
      if (this.successDiv) this.successDiv.hidden = true;
      if (this.errorDiv) this.errorDiv.hidden = true;
    }

    resetButton() {
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        this.submitBtn.textContent = 'Join Step Ahead Loyalty';
        this.submitBtn.classList.remove('cb-loyalty-signup__submit--loading');
      }
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    isValidZip(zip) {
      return /^\d{5}(-\d{4})?$/.test(zip);
    }
  }

  customElements.define('cb-loyalty-signup', CBLoyaltySignup);
  window.CBLoyaltySignup = CBLoyaltySignup;
}
